import { CancelPolicySummary, CartItemOptionUnavailableReason } from '~/__generated__/__types__'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import { DateTime } from 'luxon'
import { ICancelPolicySummary } from '~/app/components/common/CancelPolicy/CancelPolicyText'
import { toKRW } from '~/app/utils/krNumberToString'
import {
  CancelPolicyType,
  TCancelPolicyType,
} from '~/api/converters/domestic/order/ProductSection/Property/index.type'
import { CancelPolicyType as GraphQLCancelPolicyType } from '~/__generated__/__types__'

function getStatusText({
  type,
  weight,
  commissionAmount,
}: {
  type: TCancelPolicyType | undefined
  weight: number
  commissionAmount?: number
}) {
  switch (type) {
    case CancelPolicyType.FREE:
    case CancelPolicyType.EXCEPTIONAL_FREE:
      return '무료취소'
    case CancelPolicyType.TOTAL:
      return '취소 및 환불불가'
    case CancelPolicyType.RATIO:
      return `취소수수료 ${Math.ceil(weight * 10) / 10}%${
        commissionAmount ? ` ${toKRW(commissionAmount)}` : ''
      }`
    default:
      return ''
  }
}

/**
 *    @desc 취소 정책 텍스트 정보
 *    @param {ICancelPolicy} cancelPolicy 취소 정책
 *    @param {boolean} cancelable  취소 가능 여부. cancelable 이 false 이면 취소 정책이 없는 것으로 간주하여 취소 정책을 미노출한다.
 *    @param {string} nonCancellationReason 취소 불가 사유
 *    @param {number} commissionAmount  취소 수수료 금액. 취소 요청 화면에서만 노출
 *    @param multipleDaysCancelPolicyOption 연박 취소 정책. 취소 요청 화면에서만 툴팁으로 노출
 * */
export function getCancelPolicyTextInfo({
  cancelPolicy,
  cancelable = true,
  nonCancellationReason,
  commissionAmount,
  multipleDaysCancelPolicyOption,
}: ICancelPolicySummary) {
  const isCancelable = cancelable && !!cancelPolicy

  const { endAt, weight = 0 } = isCancelable ? cancelPolicy : { endAt: undefined, weight: 0 }

  const type = isCancelable
    ? cancelPolicy.type
    : nonCancellationReason === 'NON_REFUNDABLE'
      ? CancelPolicyType.TOTAL
      : undefined

  // ExceptionalFree 는 주문 완료 이후 부터 응답된다 (주문 시점을 알아야하기 때문)
  const isExceptionalFree = type === CancelPolicyType.EXCEPTIONAL_FREE

  const statusText = getStatusText({ type, weight, commissionAmount })
  const cancelValidDate = isoToFormat(endAt, 'yyyy.MM.dd (ccc) HH:mm')

  const showDate = type !== CancelPolicyType.TOTAL
  const lineBreak = type === CancelPolicyType.RATIO

  const { cancelPolicies, checkInAt = '', checkOutAt = '' } = multipleDaysCancelPolicyOption ?? {}

  const [checkOut, checkIn] = [checkOutAt, checkInAt].map(d => DateTime.fromISO(d))
  const isMultipleDays = checkOut.diff(checkIn, 'days').days > 1
  const isByDays = cancelPolicies?.method === 'BY_DAYS'

  const showTooltip =
    type === CancelPolicyType.RATIO && isMultipleDays && !!cancelPolicies && weight > 0 && isByDays

  return {
    type,
    statusText: isExceptionalFree ? `${statusText} (${cancelValidDate} 전까지)` : statusText,
    showDate,
    cancelValidDate: isExceptionalFree ? '' : cancelValidDate,
    lineBreak,
    showTooltip,
    isExceptionalFree,
  }
}

/**
 *  @desc 장바구니 > 국내숙소 상품 > 취소 정책 노출 시,
 *        상품 판매 정보 (매진, 재고없음, 판매중지) 및 체크인 시간에 따라 취소 정책을 노출하기 위한
 *        취소 정책 요약 정보를 가공하여 반환한다.
 *  @param  option 장바구니 상품 옵션
 *  @param  currentAt 현재 시간
 */
export function getDomesticAccommodationCancelSummary({
  option,
  currentAt,
}): CancelPolicySummary | undefined {
  if (
    option.unavailableReason === CartItemOptionUnavailableReason.SoldOut ||
    option.unavailableReason === CartItemOptionUnavailableReason.OutOfStock ||
    option.unavailableReason === CartItemOptionUnavailableReason.Stop
  ) {
    return {
      cancelable: false,
      nonCancellationReason: 'UNAVAILABLE',
    }
  }

  const checkInTime = DateTime.fromISO(option.checkInDate)
  const now = DateTime.fromISO(currentAt)
  if (checkInTime < now) {
    return {
      cancelable: false,
      nonCancellationReason: 'NON_REFUNDABLE',
      cancelPolicy: {
        type: CancelPolicyType.TOTAL as GraphQLCancelPolicyType,
        weight: 100,
        startAt: option.cancelPolicySummary?.cancelPolicy?.startAt ?? '',
        endAt: option.cancelPolicySummary?.cancelPolicy?.endAt ?? '',
      },
    }
  }

  return option.cancelPolicySummary
}
